/**
 * Security service -- MFA (TOTP) and passkeys for the Profile page.
 *
 * Card: "MFA (TOTP) + Passkeys (WebAuthn)" -- https://trello.com/c/lf2Z8aOo
 * Backend: api/src/endpoints/me_mfa.py + api/src/endpoints/me_passkeys.py
 *
 * Same contract as profileService.js, for the same reason: NO function
 * here takes an account id, mirroring a backend where no /me route takes
 * one. A caller cannot construct a cross-tenant request because there is
 * no parameter through which to express one.
 *
 * Secret handling
 * ===============
 * Two responses carry material that is shown ONCE and is unrecoverable
 * afterwards -- the TOTP secret from `beginMfaEnrollment` and the
 * recovery codes from `verifyMfaEnrollment` / `regenerateRecoveryCodes`.
 * Callers must render them and MUST NOT persist them to localStorage,
 * Vuex/Pinia state that outlives the dialog, or any log.
 */
import { apiCall } from './api'

// ---------------------------------------------------------------------
// TOTP
// ---------------------------------------------------------------------

/** Read the signed-in user's own MFA status. Never returns the secret. */
export function getMfaStatus() {
  return apiCall('get', '/me/mfa')
}

/**
 * Begin TOTP enrollment.
 *
 * Returns `{secret, otpauth_uri, issuer}`. This is the ONLY response that
 * ever carries the cleartext secret -- the user has to transfer it into
 * an authenticator app, so the disclosure is unavoidable, but it happens
 * exactly once and is never retrievable again.
 *
 * Does NOT enable MFA: the account is not gated until a code is proven
 * through verifyMfaEnrollment below.
 */
export function beginMfaEnrollment() {
  return apiCall('post', '/me/mfa/enroll')
}

/**
 * Prove a code and ENABLE MFA. Returns the recovery codes ONCE.
 *
 * Only salted hashes are stored server-side, so these codes cannot be
 * shown again -- present them as something the user must save now.
 */
export function verifyMfaEnrollment(code) {
  return apiCall('post', '/me/mfa/enroll/verify', { code })
}

/** Turn MFA off. The server requires the current password. */
export function disableMfa(currentPassword) {
  return apiCall('post', '/me/mfa/disable', {
    current_password: currentPassword,
  })
}

/**
 * Issue a fresh set of recovery codes, invalidating every previous one.
 *
 * The server requires the current password: this both weakens the
 * recovery path and destroys codes the user may be relying on.
 */
export function regenerateRecoveryCodes(currentPassword) {
  return apiCall('post', '/me/mfa/recovery-codes', {
    current_password: currentPassword,
  })
}

// ---------------------------------------------------------------------
// Passkeys
// ---------------------------------------------------------------------

/** List the signed-in user's own registered passkeys. */
export function listPasskeys() {
  return apiCall('get', '/me/passkeys')
}

/**
 * Begin registration -- returns WebAuthn creation options + a handle.
 *
 * Requires the caller's CURRENT PASSWORD. A passkey is a permanent,
 * password-equivalent credential that keeps working after a password
 * rotation, so the server refuses to start the ceremony on a bearer
 * token alone: an attacker with a stolen session must not be able to
 * enroll their own authenticator and outlive the victim's remediation.
 * Same bar as disabling MFA, which is the strictly less dangerous
 * operation.
 */
export function beginPasskeyRegistration(currentPassword) {
  return apiCall('post', '/me/passkeys/register', {
    current_password: currentPassword,
  })
}

/** Finish registration by submitting the authenticator's attestation. */
export function completePasskeyRegistration(challengeHandle, credential, name) {
  return apiCall('post', '/me/passkeys/register/verify', {
    challenge_handle: challengeHandle,
    credential,
    name,
  })
}

export function renamePasskey(id, name) {
  return apiCall('patch', `/me/passkeys/${id}`, { name })
}

export function deletePasskey(id) {
  return apiCall('delete', `/me/passkeys/${id}`)
}

// ---------------------------------------------------------------------
// Login-time flows (UNAUTHENTICATED -- the caller has no session yet)
// ---------------------------------------------------------------------

/**
 * Complete a login that was gated by MFA.
 *
 * `POST /auth/login` answers 401 with `mfa_required` + `mfa_challenge`
 * instead of a token when the account has a second factor. Exchange that
 * challenge plus a TOTP or recovery code here for the real session.
 */
export function verifyMfaChallenge(challenge, code) {
  return apiCall('post', '/auth/mfa/verify', { challenge, code })
}

/** Request WebAuthn options to log in with a passkey. */
export function beginPasskeyLogin(login) {
  return apiCall('post', '/auth/passkey/options', { login })
}

/** Complete a passkey login and receive a session token. */
export function completePasskeyLogin(challengeHandle, credential) {
  return apiCall('post', '/auth/passkey/verify', {
    challenge_handle: challengeHandle,
    credential,
  })
}

// ---------------------------------------------------------------------
// Browser capability
// ---------------------------------------------------------------------

/**
 * True when this browser can perform WebAuthn ceremonies.
 *
 * Checked before offering passkey UI at all: on a browser without
 * `navigator.credentials`, an "Add a passkey" button can only ever
 * throw, so it should not be rendered.
 */
export function browserSupportsPasskeys() {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    !!navigator.credentials &&
    typeof navigator.credentials.create === 'function'
  )
}

/**
 * True when the page is in a secure context, which WebAuthn REQUIRES.
 *
 * Why this is separate from `browserSupportsPasskeys`: on plain `http://`
 * (anything but localhost) the browser does not expose
 * `window.PublicKeyCredential` AT ALL, so feature detection cannot tell a
 * perfectly capable Chrome from a browser that genuinely cannot do
 * passkeys. Reporting "this browser does not support passkeys" for an
 * http page blames the wrong thing and sends the reader looking for a
 * browser or device problem that does not exist.
 *
 * Splitting the check lets the UI say which one it actually is. Note the
 * ordering that follows from it: an insecure context makes
 * `browserSupportsPasskeys()` false as a SIDE EFFECT, so callers must
 * test the context FIRST or they will always report the browser message.
 */
export function isSecureContextForPasskeys() {
  if (typeof window === 'undefined') return false
  // `isSecureContext` is the spec-defined signal and already treats
  // localhost as secure, which is what makes local dev work over http.
  return window.isSecureContext !== false
}
